import Container from '@/components/Container';
import { useTranslation } from 'react-i18next';
import SelectInput from '@/components/forms/SelectInput';
import { DMSortType, SortDirection } from '@/declarations/models/SortOption';
import { FormControl, TextField } from '@mui/material';
import { DMFilterSearchBlockModel, DMViewType } from '@/declarations/models/blocks/DMBlock';
import TextInput from '@/components/forms/TextInput';
import React from 'react';
import { settingsAreaStyle } from '@/editor/PageEditor/EditorBlock/DMFilterSearchBlock/DMFilterSearchBlock';
import SwitchInput from '@/components/forms/SwitchInput';
import { OutlinedContainer } from '@/components/OutlinedContainer';

interface ResultControlsProps {
  blockPath: string;
  currentBlock: DMFilterSearchBlockModel;
  getSortDirectionLabel: (direction: SortDirection) => string;
  maxItems: number | undefined;
  setMaxItems: (value: number | undefined) => void;
  paginateMaxItems: number | undefined;
  setPaginateMaxItems: (value: number | undefined) => void;
  gridSizeOptions: string[];
  tCommon: (key: string) => string;
  tComponents: (key: string) => string;
}

const ResultControls: React.FC<ResultControlsProps> = ({
  blockPath,
  currentBlock,
  getSortDirectionLabel,
  maxItems,
  setMaxItems,
  paginateMaxItems,
  setPaginateMaxItems,
  gridSizeOptions,
  tCommon,
  tComponents,
}) => {
  const { t: tComp } = useTranslation('components');
  const mobileGridOptions = ['auto', 'horizontal'];

  return (
    <>
      <FormControl sx={settingsAreaStyle}>
        <Container gap={3} pt={1} fullWidth left column>
          <Container fullWidth column right>
            <SwitchInput
              path={`${blockPath}.hasPictures`}
              label={tComponents('DMFilterSearch.ShowOnlyObjectsWithImage')}
            />
          </Container>
          <Container gap={2} fullWidth left>
            <SelectInput
              options={Object.values(DMSortType)}
              getOptionKey={(o) => o}
              getOptionLabel={(o) => tCommon(`DMSortTypes.${o}`)}
              path={`${blockPath}.sortBy`}
              label={tCommon('SortTypeLabel')}
              defaultValue={DMSortType.RELEVANCE}
              size='small'
              hideNoSelectionOption
            />
            <SelectInput
              options={Object.values(SortDirection)}
              getOptionKey={(o) => o}
              getOptionLabel={getSortDirectionLabel}
              path={`${blockPath}.order`}
              disabled={!currentBlock?.sortBy || currentBlock?.sortBy === DMSortType.RELEVANCE}
              label={tCommon('SortDirectionLabel')}
              defaultValue={currentBlock?.sortBy === DMSortType.TITLE ? SortDirection.ASC : SortDirection.DESC}
              size='small'
              hideNoSelectionOption
            />
          </Container>
          <Container gap={2} fullWidth left>
            <TextField
              type='number'
              size='small'
              value={maxItems ?? 0}
              onChange={(v) => setMaxItems(Number(v.target.value) || undefined)}
              fullWidth
              label={tComponents('DMBlock.MaxItemsLabel')}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 24,
                },
                inputLabel: {
                  shrink: !!Number(maxItems),
                },
              }}
            />

            <TextField
              type='number'
              size='small'
              value={paginateMaxItems ?? 0}
              onChange={(v) => setPaginateMaxItems(Number(v.target.value) || undefined)}
              fullWidth
              label={tComponents('DMBlock.PaginateMaxItemsLabel')}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 1000,
                },
                inputLabel: {
                  shrink: !!Number(paginateMaxItems),
                },
              }}
            />
          </Container>
        </Container>
      </FormControl>
      <OutlinedContainer fullWidth column left label={tComp('KPBlock.DisplayOptionsModuleLabel')}>
        <Container gap={2} fullWidth left>
          <SelectInput
            hideNoSelectionOption
            defaultValue='auto'
            options={gridSizeOptions}
            getOptionKey={(gridSize) => `size-${gridSize}`}
            getOptionLabel={(gridSize) => `${gridSize}`}
            path={`${blockPath}.gridSize`}
            label={tComponents('CardModuleBlock.GridSizeLabel')}
          />
          <SelectInput
            hideNoSelectionOption
            defaultValue='auto'
            options={mobileGridOptions}
            getOptionKey={(o) => `mobileGrid-${o}`}
            getOptionLabel={(o) => tComp(`CardModuleViewTypeSelector.CardModuleViewTypeVariant.${o}`)}
            path={`${blockPath}.gridMobileBehaviour`}
            label={tComp('CardModuleBlock.GridMobileBehaviourLabel')}
          />
        </Container>
        <Container gap={2} fullWidth left sx={{ mt: 2 }}>
          <SwitchInput path={`${blockPath}.showInSameTab`} label={tComponents('DMFilterSearch.ShowInSameTab')} />
          <SwitchInput path={`${blockPath}.integratedView`} label={tComponents('KPBlock.IntegratedView')} />
        </Container>
      </OutlinedContainer>
    </>
  );
};

export default ResultControls;
